import React, { useState } from "react";
import { Flag, Ban, MoreHorizontal, Trash2, UserX } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";

export default function MessageMenu({ message, onBlocked, isHost, roomId, onMessageDeleted, onUserBanned }) {
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const doBlock = async () => {
    try {
      await api.post(`/users/${message.user_id}/block`);
      toast.success(`Blocked ${message.username}`);
      onBlocked?.(message.user_id);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const doDelete = async () => {
    if (!window.confirm("Delete this message for everyone?")) return;
    try {
      await api.delete(`/rooms/${roomId}/messages/${message.id}`);
      onMessageDeleted?.(message.id);
      toast.success("Message removed");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const doHostBan = async () => {
    if (!window.confirm(`Ban @${message.username} from this room?`)) return;
    try {
      await api.post(`/rooms/${roomId}/host/ban/${message.user_id}`, { reason: "host action" });
      toast.success(`@${message.username} banned from this room`);
      onUserBanned?.(message.user_id);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const doReport = async () => {
    setSubmitting(true);
    try {
      await api.post(`/users/${message.user_id}/report`, {
        reason,
        message_id: message.id,
      });
      toast.success("Report submitted. Thank you.");
      setReportOpen(false);
      setReason("");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          data-testid={`msg-menu-${message.id}`}
          className="opacity-60 md:opacity-0 md:group-hover:opacity-100 transition-opacity px-1.5 py-1 rounded-md hover:bg-white/5 text-zinc-500 hover:text-zinc-300"
          aria-label="Message actions"
        >
          <MoreHorizontal className="w-3.5 h-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#121215] border-white/10 text-white min-w-[180px]">
          <DropdownMenuItem
            data-testid={`msg-report-${message.id}`}
            onClick={() => setReportOpen(true)}
            className="cursor-pointer focus:bg-white/5"
          >
            <Flag className="w-4 h-4 mr-2" /> Report
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid={`msg-block-${message.id}`}
            onClick={doBlock}
            className="cursor-pointer text-rose-400 focus:bg-white/5 focus:text-rose-300"
          >
            <Ban className="w-4 h-4 mr-2" /> Block @{message.username}
          </DropdownMenuItem>
          {isHost && (
            <>
              <DropdownMenuSeparator className="bg-white/5" />
              <DropdownMenuItem
                data-testid={`msg-delete-${message.id}`}
                onClick={doDelete}
                className="cursor-pointer text-rose-400 focus:bg-white/5 focus:text-rose-300"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete (host)
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={`msg-host-ban-${message.id}`}
                onClick={doHostBan}
                className="cursor-pointer text-rose-400 focus:bg-white/5 focus:text-rose-300"
              >
                <UserX className="w-4 h-4 mr-2" /> Ban from room (host)
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="bg-[#121215] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display">Report @{message.username}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Tell us what happened. Our team reviews every report.
            </DialogDescription>
          </DialogHeader>
          <textarea
            data-testid="report-reason-input"
            className="text-input min-h-[120px] resize-none"
            placeholder="What's wrong with this message?"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={500}
          />
          <DialogFooter>
            <button onClick={() => setReportOpen(false)} className="ghost-btn">Cancel</button>
            <button data-testid="report-submit-btn" onClick={doReport} disabled={submitting} className="neon-btn">
              {submitting ? "Sending…" : "Submit report"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
